import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  createTheme,
  styled,
  Tooltip,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useHistory, useParams } from 'react-router-dom';

import { environment } from '@env';

import landingPageTheme from '@/@mantis/theme/landingPageTheme';
import { useGetPublicCommunity } from '@/api/endpoints/community/useGetPublicCommunity';
import { useGetPublicGroupNegotiation } from '@/api/endpoints/negotiation/useGetPublicGroupNegotiation';
import { useGetShareableLink } from '@/api/endpoints/negotiation/useGetShareableLink';
import { useTrackShareClick } from '@/api/endpoints/negotiation/useTrackShareClick';
import { Globe04Icon } from '@/assets/icons/Globe04Icon';
import { Home04Icon } from '@/assets/icons/Home04Icon';
import { InfoCircleIcon } from '@/assets/icons/InfoCircleIcon';
import { Lock03Icon } from '@/assets/icons/Lock03Icon';
import { PiggyBank02Icon } from '@/assets/icons/PiggyBank02Icon';
import { Users03Icon } from '@/assets/icons/Users03Icon';
import { useAuth } from '@/context/AuthProvider';
import '@/styles/landingPage.css';

import FooterSection from './sections/FooterSection';
import NavSection from './sections/NavSection';

// Styled wrapper component following PMHeroSection practices
const SharePageBackground = styled(Box)(() => ({
  backgroundImage: 'url("/assets/images/landing/community_bg.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  minHeight: '100vh',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 0,
  },
}));

const SharePageContent = styled(Box)(() => ({
  position: 'relative',
  zIndex: 1,
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
}));

interface PricingTier {
  minHomes: number;
  maxHomes?: number;
  pricePerHome: number;
}

interface ShareMetadata {
  title?: string;
  description?: string;
  imageUrl?: string;
  communityName?: string;
  communityId?: number;
  memberCount?: number;
  savingsAmount?: number;
  originalCost?: number;
  estimated_savings?: number;
  groupSize?: number;
  activeGroupCount?: number;
  pricingTiers?: PricingTier[];
}

// Custom wrapper for the share page
const SharePageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = createTheme(landingPageTheme);

  useEffect(() => {
    const fontPreloadLinks = [
      { href: '/assets/fonts/Nohemi-Regular.woff2', type: 'font/woff2' },
      { href: '/assets/fonts/Nohemi-SemiBold.woff', type: 'font/woff' },
    ];

    fontPreloadLinks.forEach(font => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = font.href;
      link.as = 'font';
      link.type = font.type;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });

    return () => {
      fontPreloadLinks.forEach(font => {
        const links = document.querySelectorAll(`link[href="${font.href}"]`);
        links.forEach(link => link.remove());
      });
    };
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <SharePageBackground>
          <SharePageContent>{children}</SharePageContent>
        </SharePageBackground>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

const SharePage: React.FC = () => {
  const history = useHistory();
  const { shortCode } = useParams<{ shortCode: string }>();
  const { session } = useAuth();
  const { data: shareData, isLoading, error } = useGetShareableLink(shortCode);
  const trackClickMutation = useTrackShareClick();

  const rawMetadata = shareData?.metadata as ShareMetadata | undefined;
  // Handle both community_id and communityId in metadata
  const metadata = rawMetadata
    ? {
      ...rawMetadata,
      communityId:
          rawMetadata.communityId ||
          (rawMetadata as ShareMetadata & { community_id?: number }).community_id,
    }
    : undefined;
  const campaign = 'share_link';

  // Fetch public data based on share type
  const { data: publicCommunityData } = useGetPublicCommunity(
    shareData?.target_type === 'community' ? Number(shareData.target_id) : 0,
    shareData?.target_type === 'community'
  );

  const { data: publicGroupData } = useGetPublicGroupNegotiation(
    shareData?.target_type === 'group_negotiation' ? Number(shareData.target_id) : 0,
    shareData?.target_type === 'group_negotiation'
  );

  // Use public data if available, fall back to metadata
  let title = 'Join Our Community on Terr.ai';
  let description = 'Connect with your neighbors and save together on Terr.ai';

  if (shareData?.target_type === 'community' && publicCommunityData) {
    title = `Join ${publicCommunityData.name} on Terr.ai`;
    description =
      publicCommunityData.description ||
      `Connect with ${publicCommunityData.memberCount} neighbor${
        publicCommunityData.memberCount === 1 ? '' : 's'
      } in your building. ${publicCommunityData.activeGroupCount} active Save Together groups.`;
  } else if (shareData?.target_type === 'group_negotiation' && publicGroupData) {
    title = `Join ${publicGroupData.memberCount} neighbor${
      publicGroupData.memberCount === 1 ? '' : 's'
    } in ${publicGroupData.communityName}`;
    description = '';
  } else if (rawMetadata) {
    // Fall back to metadata if public data not available
    title = rawMetadata.title || title;
    description = rawMetadata.description || description;
  }

  const imageUrl = metadata?.imageUrl || `${environment.frontEnd}/assets/images/terrai-logo.png`;

  useEffect(() => {
    localStorage.setItem('campaign', campaign);
  }, []);

  const handleContinue = () => {
    // Track the click if user is authenticated
    if (session && shortCode && !trackClickMutation.isPending) {
      trackClickMutation.mutate(shortCode);
    }

    // If not authenticated, redirect to login with share link preserved
    if (!session) {
      localStorage.setItem('shareRedirectUrl', window.location.pathname);

      // Build referral parameters for signup
      const referralParams = new URLSearchParams({
        referral_type: shareData?.target_type || '',
        referral_id: shareData?.target_id?.toString() || '',
        referral_url: window.location.pathname,
        campaign: 'share_link',
      });

      // Add community_id from public data or metadata
      let communityId: number | undefined;
      if (shareData?.target_type === 'group_negotiation') {
        communityId = publicGroupData?.communityId || metadata?.communityId;
      } else if (shareData?.target_type === 'community') {
        communityId = Number(shareData.target_id);
      }

      if (communityId) {
        referralParams.append('community_id', communityId.toString());
      }

      history.push(`/login?${referralParams.toString()}`);
    } else if (shareData?.target_type === 'group_negotiation') {
      const communityId = publicGroupData?.communityId || metadata?.communityId;
      if (communityId && shareData.target_id) {
        const redirectUrl = `/property-manager/communities/${communityId}/groups/${shareData.target_id}?from=share`;
        history.push(redirectUrl);
      } else {
        // Stay on the share page if data is not available yet
        // Group negotiation data not available for redirect - no action needed
      }
    } else if (shareData?.target_type === 'community') {
      const redirectUrl = `/property-manager/communities/${shareData.target_id}?from=share`;
      history.push(redirectUrl);
    } else if (shareData?.target_type) {
      // Unknown share type - stay on share page
      // No redirect needed for unknown share types
    }
  };

  if (isLoading) {
    return (
      <SharePageWrapper>
        <Helmet>
          <title>Loading...</title>
        </Helmet>
        <NavSection simplified campaign={campaign} />
        <Container
          maxWidth="lg"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Container>
        <FooterSection />
      </SharePageWrapper>
    );
  }

  if (error || !shareData) {
    return (
      <SharePageWrapper>
        <Helmet>
          <title>Invalid Link - Terr.ai</title>
        </Helmet>
        <NavSection simplified campaign={campaign} />
        <Container
          maxWidth="lg"
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Typography variant="h4" color="error">
              Invalid or Expired Link
            </Typography>
            <Typography sx={{ color: '#fff' }}>This share link is no longer valid.</Typography>
            <Button variant="contained" href="/">
              Go to Homepage
            </Button>
          </Stack>
        </Container>
        <FooterSection />
      </SharePageWrapper>
    );
  }

  return (
    <SharePageWrapper>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />

        {/* Open Graph tags for Facebook, LinkedIn */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={`${environment.frontEnd}/share/${shortCode}`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Terr.ai" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@terrai" />

        {/* Additional meta tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${environment.frontEnd}/share/${shortCode}`} />
      </Helmet>

      <NavSection simplified campaign={campaign} />

      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Stack spacing={6} alignItems="center">
          {/* Hero Section */}
          <Stack spacing={3} alignItems="center" textAlign="center" sx={{ pt: 12 }}>
            <Typography
              variant="landingH1"
              sx={{
                fontSize: { xs: '26px', sm: '48px', md: '54px' },
                color: '#fff',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="landingBody"
              sx={{
                maxWidth: '650px',
                color: '#fff',
              }}
            >
              {description}
            </Typography>
          </Stack>

          {/* Content Card */}
          <Card
            sx={{
              width: '100%',
              maxWidth: '650px',
              boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)',
              borderRadius: 3,
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={4}>
                {shareData.target_type === 'group_negotiation' && (
                  <>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Users03Icon sx={{ width: 48, height: 48, color: '#9D1500' }} />
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {publicGroupData?.title || 'Save Together'}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ whiteSpace: 'pre-wrap' }}
                        >
                          {publicGroupData?.description || 'Join neighbors to save together'}
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Pricing Tiers with Behavioral Incentivizing */}
                    {((publicGroupData?.pricingTiers && publicGroupData.pricingTiers.length > 0) ||
                      (metadata?.pricingTiers && metadata.pricingTiers.length > 0)) && (
                      <Box
                        sx={{
                          p: 3,
                          bgcolor: 'success.50',
                          borderRadius: 2,
                          border: '2px solid',
                          borderColor: 'success.200',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Background pattern */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -30,
                            right: -30,
                            width: 120,
                            height: 120,
                            borderRadius: '50%',
                            bgcolor: 'rgba(76, 175, 80, 0.1)',
                          }}
                        />

                        <Stack spacing={3} position="relative">
                          <Box textAlign="center">
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color="success.900"
                              sx={{ mb: 0.5 }}
                            >
                              🎯 Group Savings Program
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              The more people who join, the greater the savings for everyone!
                            </Typography>
                          </Box>

                          {(() => {
                            const currentMemberCount =
                              publicGroupData?.memberCount ?? metadata?.memberCount ?? 0;

                            // Use pricing tiers from publicGroupData if available, otherwise from metadata
                            const pricingTiers =
                              publicGroupData?.pricingTiers || metadata?.pricingTiers || [];

                            // Find current tier
                            const currentTier = pricingTiers.find(
                              tier =>
                                currentMemberCount >= tier.minHomes &&
                                (!tier.maxHomes || currentMemberCount <= tier.maxHomes)
                            );

                            // Find next tier
                            const nextTier = pricingTiers.find(
                              tier => currentMemberCount < tier.minHomes
                            );

                            // Calculate savings
                            const highestPrice = pricingTiers[0]?.pricePerHome || 0;
                            const currentPrice = currentTier?.pricePerHome || highestPrice;
                            const currentSavings = highestPrice - currentPrice;
                            const savingsPercentage =
                              highestPrice > 0
                                ? Math.round((currentSavings / highestPrice) * 100)
                                : 0;

                            return (
                              <>
                                {/* Current Status */}
                                <Box
                                  sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    bgcolor: 'white',
                                    border: '1px solid',
                                    borderColor: 'success.200',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                  }}
                                >
                                  <Stack spacing={2}>
                                    <Stack
                                      direction="row"
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Box>
                                        <Typography variant="caption" color="text.secondary">
                                          Current Group Size
                                        </Typography>
                                        <Typography
                                          variant="h5"
                                          fontWeight={700}
                                          color="success.700"
                                        >
                                          {currentMemberCount}{' '}
                                          {currentMemberCount === 1 ? 'Member' : 'Members'}
                                        </Typography>
                                      </Box>
                                      <Box textAlign="right">
                                        <Typography variant="caption" color="text.secondary">
                                          Current Price
                                        </Typography>
                                        <Typography
                                          variant="h5"
                                          fontWeight={700}
                                          color="success.700"
                                        >
                                          ${currentPrice}/home
                                        </Typography>
                                        {savingsPercentage > 0 && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: 'success.600',
                                              fontWeight: 600,
                                              display: 'block',
                                            }}
                                          >
                                            Save {savingsPercentage}%
                                          </Typography>
                                        )}
                                      </Box>
                                    </Stack>

                                    {/* Progress to next tier */}
                                    {nextTier && (
                                      <Box>
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          sx={{ mb: 1 }}
                                        >
                                          <Typography variant="caption" color="text.secondary">
                                            Progress to next tier
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            fontWeight={600}
                                            color="warning.700"
                                          >
                                            {nextTier.minHomes - currentMemberCount} more to unlock
                                            ${nextTier.pricePerHome}/home
                                          </Typography>
                                        </Stack>
                                        <Box sx={{ position: 'relative' }}>
                                          <Box
                                            sx={{
                                              height: 6,
                                              borderRadius: 3,
                                              bgcolor: 'grey.200',
                                              overflow: 'hidden',
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                height: '100%',
                                                width: `${
                                                  (currentMemberCount / nextTier.minHomes) * 100
                                                }%`,
                                                bgcolor: 'warning.main',
                                                transition: 'width 0.3s ease',
                                              }}
                                            />
                                          </Box>
                                        </Box>
                                      </Box>
                                    )}
                                  </Stack>
                                </Box>

                                {/* All Pricing Tiers */}
                                <Stack spacing={1}>
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                    color="text.primary"
                                  >
                                    All Pricing Tiers
                                  </Typography>
                                  {pricingTiers.map((tier, index) => {
                                    const isCurrentTier =
                                      currentMemberCount >= tier.minHomes &&
                                      (!tier.maxHomes || currentMemberCount <= tier.maxHomes);
                                    const isAchieved = currentMemberCount >= tier.minHomes;
                                    const tierSavings = highestPrice - tier.pricePerHome;
                                    const tierSavingsPercent =
                                      highestPrice > 0
                                        ? Math.round((tierSavings / highestPrice) * 100)
                                        : 0;

                                    return (
                                      <Box
                                        key={`tier-${tier.minHomes}-${tier.maxHomes || 'plus'}`}
                                        sx={{
                                          p: 2,
                                          borderRadius: 1.5,
                                          bgcolor: (() => {
                                            if (isCurrentTier) return 'success.100';
                                            if (isAchieved) return 'grey.50';
                                            return 'white';
                                          })(),
                                          border: '2px solid',
                                          borderColor: (() => {
                                            if (isCurrentTier) return 'success.500';
                                            if (isAchieved) return 'grey.300';
                                            return 'grey.200';
                                          })(),
                                          position: 'relative',
                                          transition: 'all 0.2s',
                                          ...(isCurrentTier && {
                                            transform: 'scale(1.02)',
                                            boxShadow: '0 2px 8px rgba(76, 175, 80, 0.2)',
                                          }),
                                        }}
                                      >
                                        <Stack
                                          direction="row"
                                          justifyContent="space-between"
                                          alignItems="center"
                                        >
                                          <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Box
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: isAchieved ? 'success.100' : 'grey.100',
                                                color: isAchieved ? 'success.700' : 'grey.500',
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                              }}
                                            >
                                              {isAchieved ? '✓' : index + 1}
                                            </Box>
                                            <Box>
                                              <Typography
                                                variant="body2"
                                                fontWeight={isCurrentTier ? 700 : 500}
                                                color={
                                                  isCurrentTier ? 'success.900' : 'text.primary'
                                                }
                                              >
                                                {tier.maxHomes
                                                  ? `${tier.minHomes}-${tier.maxHomes} Homes`
                                                  : `${tier.minHomes}+ Homes`}
                                              </Typography>
                                              {isCurrentTier && (
                                                <Typography
                                                  variant="caption"
                                                  color="success.700"
                                                  fontWeight={600}
                                                >
                                                  ✨ Current tier
                                                </Typography>
                                              )}
                                            </Box>
                                          </Stack>
                                          <Stack alignItems="flex-end">
                                            <Typography
                                              variant="body1"
                                              fontWeight={700}
                                              color={isCurrentTier ? 'success.700' : 'text.primary'}
                                            >
                                              ${tier.pricePerHome}/home
                                            </Typography>
                                            {tierSavingsPercent > 0 && (
                                              <Typography
                                                variant="caption"
                                                color={
                                                  isAchieved ? 'success.600' : 'text.secondary'
                                                }
                                                fontWeight={500}
                                              >
                                                Save {tierSavingsPercent}%
                                              </Typography>
                                            )}
                                          </Stack>
                                        </Stack>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </>
                            );
                          })()}
                        </Stack>
                      </Box>
                    )}

                    {/* One-Time Price Savings with Behavioral Incentivizing */}
                    {(() => {
                      const originalCost = metadata?.originalCost || 0;
                      const savingsAmount =
                        metadata?.savingsAmount || metadata?.estimated_savings || 0;
                      const hasSinglePrice =
                        originalCost > 0 &&
                        savingsAmount > 0 &&
                        (!metadata?.pricingTiers || metadata.pricingTiers.length === 0);

                      if (!hasSinglePrice) return null;

                      const negotiatedCost = originalCost - savingsAmount;
                      const savingsPercentage = Math.round((savingsAmount / originalCost) * 100);
                      const memberCount =
                        publicGroupData?.memberCount ?? metadata?.memberCount ?? 0;

                      return (
                        <Box
                          sx={{
                            p: 3,
                            bgcolor: 'error.50',
                            borderRadius: 2,
                            border: '2px solid',
                            borderColor: 'error.200',
                            position: 'relative',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Urgency gradient background */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: '40%',
                              height: '100%',
                              background:
                                'linear-gradient(90deg, transparent 0%, rgba(211, 47, 47, 0.05) 100%)',
                            }}
                          />

                          <Stack spacing={3} position="relative">
                            {/* Header with urgency messaging */}
                            <Box textAlign="center">
                              <Typography
                                variant="h6"
                                fontWeight={700}
                                color="error.900"
                                sx={{ mb: 0.5 }}
                              >
                                💰 Limited-Time Group Deal
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {memberCount > 0
                                  ? `Join ${memberCount} neighbor${
                                    memberCount === 1 ? '' : 's'
                                  } who ${
                                    memberCount === 1 ? 'has' : 'have'
                                  } already locked in this price!`
                                  : 'Be the first to join and help your neighbors save!'}
                              </Typography>
                            </Box>

                            {/* Savings showcase */}
                            <Box
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                bgcolor: 'white',
                                border: '2px solid',
                                borderColor: 'error.300',
                                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.1)',
                              }}
                            >
                              <Stack spacing={2}>
                                {/* Price comparison */}
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  justifyContent="space-between"
                                  alignItems={{ xs: 'stretch', sm: 'center' }}
                                  spacing={2}
                                >
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ textDecoration: 'line-through' }}
                                    >
                                      Individual Price
                                    </Typography>
                                    <Typography
                                      variant="h5"
                                      sx={{
                                        textDecoration: 'line-through',
                                        color: 'text.disabled',
                                        fontWeight: 500,
                                      }}
                                    >
                                      ${originalCost.toFixed(2)}
                                    </Typography>
                                  </Box>

                                  <Box textAlign={{ xs: 'left', sm: 'center' }}>
                                    <Box
                                      sx={{
                                        display: 'inline-block',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 20,
                                        bgcolor: 'error.100',
                                        mb: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="error.700"
                                      >
                                        SAVE {savingsPercentage}%
                                      </Typography>
                                    </Box>
                                    <Typography variant="h4" fontWeight={800} color="error.700">
                                      ${savingsAmount.toFixed(2)} OFF
                                    </Typography>
                                  </Box>

                                  <Box textAlign={{ xs: 'left', sm: 'right' }}>
                                    <Typography variant="body2" color="text.secondary">
                                      Group Price
                                    </Typography>
                                    <Typography variant="h4" fontWeight={700} color="success.700">
                                      ${negotiatedCost.toFixed(2)}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="success.600"
                                      fontWeight={600}
                                    >
                                      per member
                                    </Typography>
                                  </Box>
                                </Stack>

                                {/* Social proof */}
                                <Box
                                  sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    bgcolor: 'warning.50',
                                    border: '1px solid',
                                    borderColor: 'warning.200',
                                  }}
                                >
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      color="warning.800"
                                    >
                                      ⏰ This special group rate won't last forever
                                    </Typography>
                                  </Stack>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mt: 0.5 }}
                                  >
                                    Join now to secure your spot and help the group reach even
                                    better savings
                                  </Typography>
                                </Box>

                                {/* Psychological anchoring */}
                                <Stack spacing={1}>
                                  <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color="text.secondary"
                                  >
                                    What your savings could buy:
                                  </Typography>
                                  <Stack direction="row" spacing={2} flexWrap="wrap">
                                    {savingsAmount >= 50 && (
                                      <Typography variant="caption" color="text.secondary">
                                        ☕ {Math.floor(savingsAmount / 5)} cups of coffee
                                      </Typography>
                                    )}
                                    {savingsAmount >= 100 && (
                                      <Typography variant="caption" color="text.secondary">
                                        🍕 {Math.floor(savingsAmount / 20)} pizzas
                                      </Typography>
                                    )}
                                    {savingsAmount >= 200 && (
                                      <Typography variant="caption" color="text.secondary">
                                        ⛽ {Math.floor(savingsAmount / 40)} tanks of gas
                                      </Typography>
                                    )}
                                  </Stack>
                                </Stack>
                              </Stack>
                            </Box>

                            {/* Call to action with urgency */}
                            <Box textAlign="center">
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                color="error.700"
                                sx={{ mb: 1 }}
                              >
                                Don't miss out on these community savings!
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                The more neighbors join, the stronger our negotiating power becomes
                              </Typography>
                            </Box>
                          </Stack>
                        </Box>
                      );
                    })()}
                  </>
                )}

                {shareData.target_type === 'community' && (
                  <>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Home04Icon sx={{ width: 48, height: 48, color: '#9D1500' }} />
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Community
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Join your community
                        </Typography>
                      </Box>
                    </Stack>

                    {(publicCommunityData?.name || metadata?.communityName) && (
                      <Stack spacing={1} alignItems="center">
                        <Typography variant="h5" fontWeight={600} textAlign="center">
                          {publicCommunityData?.name || metadata?.communityName}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {publicCommunityData?.require_verification ? (
                            <>
                              <Lock03Icon
                                sx={{
                                  fontSize: 16,
                                  color: 'text.secondary',
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Private Community
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Globe04Icon
                                sx={{
                                  fontSize: 16,
                                  color: 'text.secondary',
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                Public Community
                              </Typography>
                            </>
                          )}
                        </Stack>
                      </Stack>
                    )}

                    <Stack direction="row" spacing={4} justifyContent="center">
                      {(publicCommunityData?.memberCount ?? metadata?.memberCount) !==
                        undefined && (
                        <Box
                          textAlign="center"
                          sx={{
                            p: 3,
                            bgcolor: 'primary.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'primary.100',
                            minWidth: 140,
                          }}
                        >
                          <Stack spacing={1} alignItems="center">
                            <Users03Icon
                              sx={{
                                fontSize: 32,
                                color: 'primary.main',
                                mb: 1,
                              }}
                            />
                            <Typography variant="h4" fontWeight={600} color="primary.main">
                              {publicCommunityData?.memberCount ?? metadata?.memberCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Active Members
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                      {(publicCommunityData?.activeGroupCount ?? metadata?.activeGroupCount) !==
                        undefined && (
                        <Box
                          textAlign="center"
                          sx={{
                            p: 3,
                            bgcolor: 'success.50',
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'success.100',
                            minWidth: 140,
                            position: 'relative',
                          }}
                        >
                          <Tooltip
                            title="Groups created by community members to get bulk discounts on services or products"
                            placement="top"
                            arrow
                          >
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                cursor: 'help',
                              }}
                            >
                              <InfoCircleIcon
                                sx={{
                                  fontSize: 20,
                                  color: 'text.secondary',
                                  '&:hover': {
                                    color: 'success.main',
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Stack spacing={1} alignItems="center">
                            <PiggyBank02Icon
                              sx={{
                                fontSize: 32,
                                color: 'success.main',
                                mb: 1,
                              }}
                            />
                            <Typography variant="h4" fontWeight={600} color="success.main">
                              {publicCommunityData?.activeGroupCount ?? metadata?.activeGroupCount}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Save Together Groups
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </>
                )}

                {shareData.target_type === 'community' &&
                publicCommunityData?.is_active === false ? (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'grey.100',
                        borderRadius: 2,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="body1" color="text.secondary" fontWeight={500}>
                      This community is no longer active
                      </Typography>
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleContinue}
                      sx={{
                        py: 2,
                        fontSize: '18px',
                        fontWeight: 600,
                        bgcolor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      }}
                    >
                      {session ? 'Continue to Terr.ai' : 'Join Now'}
                    </Button>
                  )}
              </Stack>
            </CardContent>
          </Card>

          {/* Learn More CTA */}
          <Box
            sx={{
              width: '100%',
              maxWidth: '600px',
              textAlign: 'center',
              mt: 4,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: '#fff',
                mb: 2,
              }}
            >
              Want to learn more about how Terr.ai helps communities save together?
            </Typography>
            <Button
              variant="outlined"
              size="large"
              href="/"
              sx={{
                borderColor: '#fff',
                color: '#fff',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#fff',
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Learn More About Terr.ai
            </Button>
          </Box>
        </Stack>
      </Container>

      <FooterSection />
    </SharePageWrapper>
  );
};

export default SharePage;
